<template>
  <div class="form">
    <Skeleton v-if="!loaded" />
    <ClientOnly>
      <ais-instant-search
        v-show="loaded"
        :search-client="searchClient"
        index-name="post-documents:published_date_timestamp:asc"
        :future="{
          preserveSharedStateOnUnmount: true
        }"
      >
        <ais-configure :page="0" :hits-per-page="itemsPerPage" :filters="filterByBlog" />
        <div class="container space-y-5 lg:space-y-10">
          <div class="grid grid-cols-12 gap-6">
            <div v-if="windowSizes.lg" class="col-span-12 lg:col-span-3">
              <ais-custom-refinement-list
                ref="filters"
                sorting-facets="alpha"
                :facets="DOCUMENTS_FACETS"
                class="panel-filters"
                scroll-section-id="documents-section"
                @change="onChangeFilter"
              />
            </div>
            <div class="flex flex-col col-span-12 lg:col-span-9">
              <div class="flex">
                <button
                  v-if="!windowSizes.lg"
                  class="btn-filter"
                  type="button"
                  @click="showFilters = !showFilters"
                >
                  <icon name="pt-sliders-v-alt" class="text-xl text-gray" />
                </button>

                <ais-search-box
                  :class-names="{
                    'ais-SearchBox': 'pt-form-search-box w-full'
                  }"
                >
                  <template #default="{ refine, currentRefinement }">
                    <div class="pt-search__icon">
                      <icon name="pt-search" />
                    </div>
                    <input
                      type="search"
                      tabindex="0"
                      :value="currentRefinement"
                      :placeholder="$t('ui.placeholder.searchDocuments')"
                      @input="refine($event.currentTarget.value)"
                    />
                    <button
                      type="reset"
                      class="ais-SearchBox-reset"
                      :class="{ hidden: currentRefinement === '' }"
                      @click="refine('')"
                    >
                      <icon name="pt-close" />
                    </button>
                  </template>
                </ais-search-box>
              </div>

              <ais-custom-refinement-list
                v-if="!windowSizes.lg"
                ref="filters"
                :facets="DOCUMENTS_FACETS"
                :class="['panel-filters mb-5', showFilters ? 'block' : 'hidden']"
                scroll-section-id="documents-section"
                @change="onChangeFilter"
              />
              <div class="panel-result-info">
                <ais-stats>
                  <template #default="{ hitsPerPage, nbHits, page }">
                    <div>
                      <markdown
                        class="text-sm text-gray-dark"
                        :content="
                          nbHits > 0
                            ? $t('ui.label.aisStats', {
                                currentItem: page * hitsPerPage + 1,
                                lastItem:
                                  nbHits > hitsPerPage && page * hitsPerPage + hitsPerPage < nbHits
                                    ? page * hitsPerPage + hitsPerPage
                                    : nbHits,
                                nbHits,
                                tag: '<strong>',
                                tagEnd: '</strong>'
                              })
                            : $t('ui.label.aisStatsNotResult', {
                                tag: '<strong>',
                                tagEnd: '</strong>'
                              })
                        "
                      />
                    </div>
                  </template>
                </ais-stats>
                <ais-hits-per-page
                  ref="aisHitsPerPage"
                  :items="createHitsPerPage([10, 15, 20, 25, 30], itemsPerPage)"
                />
              </div>
              <ais-hits :transform-items="transformItems">
                <template #default="{ items }">
                  <div @click.once="loaded = true">
                    <div v-if="items.length" v-appear @click.once="loaded = true">
                      <button
                        v-for="(document, index) in items"
                        :key="index"
                        type="button"
                        class="flex items-center justify-between text-gray mb-2 pl-5 py-2 w-full text-left even:bg-white rounded"
                        @click="downloadDocument(document)"
                      >
                        <div class="flex">
                          <icon name="pt-PDFnoun" class="text-red text-xl mr-3" />
                          <ais-highlight
                            class="hover:underline text-gray-dark"
                            :hit="document"
                            attribute="title"
                          />
                        </div>
                        <span class="font-ubuntu font-medium text-gray-dark pl-10 pr-4">{{
                          dateToString(document.published_date)
                        }}</span>
                      </button>
                      <ais-custom-pagination
                        class="self-baseline mt-8"
                        scroll-section-id="documents-section"
                      />
                    </div>
                    <p v-else class="font-ubuntu font-bold text-blue text-xl text-center">
                      {{ $t('community.aisStatsNotResultDocuments') }}
                    </p>
                  </div>
                </template>
              </ais-hits>
            </div>
          </div>
        </div>
      </ais-instant-search>
    </ClientOnly>
  </div>
</template>
<script>
import { DOCUMENTS_FACETS } from '~/utils/enums';
import { downloadFileCrossBrowser } from '@/utils/helpers/script';
import { dateToString } from '~/utils/helpers/date';
import Markdown from '@/components/Markdown';
import Skeleton from '@/components/Skeleton/MeilisearchCategories';
import AisCustomRefinementList from '@/components/Instantsearch/RefinementList';
import AisCustomPagination from '@/components/Instantsearch/Pagination';
export default {
  name: 'PtMeilisearchDocuments',
  components: {
    Markdown,
    Skeleton,
    AisCustomRefinementList,
    AisCustomPagination
  },
  data() {
    const { windowWidth } = useWindow();
    const { searchClient, transformItems, createHitsPerPage } = useInstantSearch();
    return {
      show: false,
      showFilters: false,
      loaded: false,
      facetsMenu: [],
      itemsPerPage: 20,
      customFilters: '',
      DOCUMENTS_FACETS,
      dateToString,
      windowWidth,
      searchClient,
      transformItems,
      createHitsPerPage
    };
  },
  computed: {
    windowSizes() {
      return {
        lg: this.windowWidth >= 1024
      };
    },
    filterByBlog() {
      return `blog_slug_facets = ${this.$route.params.blog}${this.customFilters ? ` AND (${this.customFilters})` : ''}`;
    }
  },
  beforeUpdate() {
    if (this.loaded) {
      this.$watch(
        () => this.$refs.aisHitsPerPage?.$data.selected,
        val => {
          this.itemsPerPage = val;
        }
      );
    }
  },
  methods: {
    onChangeFilter(filters) {
      this.customFilters = filters ? [...new Set(filters.flat(Infinity))].join(' AND ') : '';
    },
    downloadDocument(document) {
      downloadFileCrossBrowser(document.file.url);
    }
  }
};
</script>
